import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import {
  Contact,
  ContactInteraction,
  CommunicationChannel,
  ApiResponse,
  PaginatedResponse,
} from 'types';

// Mock data for development
const mockContacts: Contact[] = [
  {
    _uid: 'contact-1',
    name: 'John Doe',
    company: 'Tech Corp',
    phone_numbers: [
      {
        number: '+1234567891',
        type: 'mobile',
        is_primary: true,
        verified: true,
        whatsapp_enabled: true,
        rcs_enabled: false,
      },
      {
        number: '+1234567892',
        type: 'work',
        is_primary: false,
        verified: true,
        whatsapp_enabled: false,
        rcs_enabled: false,
      },
    ],
    email_addresses: [
      {
        email: 'john.doe@techcorp.com',
        type: 'work',
        is_primary: true,
        verified: true,
      },
    ],
    channels: [
      {
        channel: 'telephony',
        last_contact_at: '2024-01-20T10:30:00Z',
        unread_count: 0,
        is_active: true,
      },
      {
        channel: 'whatsapp',
        channel_id: 'wa_1234567891',
        last_contact_at: '2024-01-20T15:30:00Z',
        unread_count: 2,
        is_active: true,
      },
      {
        channel: 'email',
        last_contact_at: '2024-01-19T09:00:00Z',
        unread_count: 0,
        is_active: true,
      },
    ],
    labels: ['customer', 'vip', 'tech'],
    total_interactions: 45,
    last_contact_at: '2024-01-20T15:30:00Z',
    last_contact_channel: 'whatsapp',
    is_assigned: true,
    assigned_user: 'agent-1',
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-20T15:30:00Z',
  },
  {
    _uid: 'contact-2',
    name: 'Jane Smith',
    company: 'Design Studio',
    phone_numbers: [
      {
        number: '+9876543210',
        type: 'mobile',
        is_primary: true,
        verified: true,
        whatsapp_enabled: true,
        rcs_enabled: true,
      },
    ],
    email_addresses: [
      {
        email: 'jane@designstudio.com',
        type: 'work',
        is_primary: true,
        verified: true,
      },
      {
        email: 'jane.smith@personal.com',
        type: 'personal',
        is_primary: false,
        verified: false,
      },
    ],
    channels: [
      {
        channel: 'telephony',
        last_contact_at: '2024-01-18T14:20:00Z',
        unread_count: 0,
        is_active: true,
      },
      {
        channel: 'whatsapp',
        channel_id: 'wa_9876543210',
        last_contact_at: '2024-01-19T15:20:00Z',
        unread_count: 0,
        is_active: true,
      },
      {
        channel: 'sms',
        last_contact_at: '2024-01-17T11:00:00Z',
        unread_count: 0,
        is_active: true,
      },
    ],
    labels: ['lead', 'design'],
    total_interactions: 12,
    last_contact_at: '2024-01-19T15:20:00Z',
    last_contact_channel: 'whatsapp',
    is_assigned: false,
    created_at: '2024-01-18T09:00:00Z',
    updated_at: '2024-01-19T15:20:00Z',
  },
];

/**
 * The channels a contact list can be filtered by.
 *
 * There is no channel table behind this. A contact's channels are not stored;
 * they are implied by the rows hanging off it, and the list below is exactly the
 * four the server can answer for — the same four facts the Channels column draws
 * its badges from, so filtering by one returns precisely the contacts showing
 * that badge:
 *
 *   telephony  a row in contact_phones
 *   whatsapp   contact_phones.whatsapp_capable
 *   rcs        contact_phones.rcs_capable
 *   email      a row in contact_emails
 *
 * SMS is deliberately absent. Nothing links an SMS to a contact — sms_messages
 * carries a to_number and no contact_id — so the transform never derives an SMS
 * badge and the API refuses `channel=sms` by name. The button shipped anyway and
 * returned all 5,822 contacts, presented as the SMS subset.
 */
export const CHANNEL_FILTERS: { channel: CommunicationChannel; label: string }[] = [
  { channel: 'telephony', label: 'Telephony' },
  { channel: 'whatsapp', label: 'WhatsApp' },
  { channel: 'rcs', label: 'RCS' },
  { channel: 'email', label: 'Email' },
];

/**
 * Channels the UI names but cannot filter on, with the reason shown to whoever
 * hovers the control. Offered disabled rather than removed: an operator who used
 * the SMS button yesterday is owed an explanation, not a vanished button.
 */
export const UNFILTERABLE_CHANNELS: { channel: CommunicationChannel; label: string; reason: string }[] = [
  {
    channel: 'sms',
    label: 'SMS',
    reason: 'SMS is not recorded against contacts, so there is nothing to filter on yet',
  },
];

/**
 * Exactly what GET /v1/contacts binds, and nothing else.
 *
 * Spring drops a query parameter it does not bind without a word, so a name the
 * API never agreed to looks like a working feature forever. Two of them were
 * live here at once: `perPage` (the endpoint reads `size`, so every page came
 * back with 20 rows while the pager was told 10) and `channel` (the endpoint had
 * no such parameter, so every channel button returned all 5,822 contacts).
 *
 * The page size is called `size` here for the single reason that `size` is what
 * goes on the wire. Keeping a friendlier alias is what hid the first bug; a stale
 * `perPage` is now a compile error instead of a silent 20.
 */
export interface GetContactsParams {
  /** 1-based, as the UI counts. Converted to the backend's 0-based `page` on the way out. */
  page?: number;
  /** Rows per page. The wire name — do not add a `perPage` alias. */
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  /** telephony | whatsapp | rcs | email. `sms` is refused by the API — see CHANNEL_FILTERS. */
  channel?: CommunicationChannel;
  assignedTo?: string; // filter to conversations assigned to this user id ("My Leads")
  /** Chat-list filters, all server-side. */
  unreadOnly?: boolean;
  recentDays?: number;
  includeAutoReplies?: boolean;
  organizationId?: string;
}

/**
 * The query string for a contacts list request.
 *
 * Split out from the request so the naming can be tested without a network call —
 * every bug this function exists to prevent was a name mismatch, and a name
 * mismatch is invisible in an integration test that only asserts a 200.
 */
export function contactListQuery(params: GetContactsParams = {}): Record<string, any> {
  const { page, ...rest } = params;
  return {
    ...rest,
    // The UI counts pages from 1; the backend counts from 0.
    page: page !== undefined ? page - 1 : 0,
  };
}

class ContactsService {
  // Transform backend contact to frontend format
  private transformContact(backendContact: any): Contact {
    // Derive channels from phone numbers
    const channels: any[] = [];

    // Add telephony channel if there are phone numbers
    if (backendContact.phoneNumbers && backendContact.phoneNumbers.length > 0) {
      channels.push({
        channel: 'telephony' as CommunicationChannel,
        last_contact_at: backendContact.lastContactedAt ? new Date(backendContact.lastContactedAt).toISOString() : undefined,
        unread_count: 0,
        is_active: true,
      });
    }

    // Add WhatsApp channel for WhatsApp-capable numbers
    const hasWhatsApp = backendContact.phoneNumbers?.some((p: any) => p.whatsappCapable);
    if (hasWhatsApp) {
      channels.push({
        channel: 'whatsapp' as CommunicationChannel,
        unread_count: 0,
        is_active: true,
      });
    }

    // Add RCS channel for RCS-capable numbers
    const hasRCS = backendContact.phoneNumbers?.some((p: any) => p.rcsCapable);
    if (hasRCS) {
      channels.push({
        channel: 'rcs' as CommunicationChannel,
        unread_count: 0,
        is_active: true,
      });
    }

    // Add email channel if there are emails
    if (backendContact.emails && backendContact.emails.length > 0) {
      channels.push({
        channel: 'email' as CommunicationChannel,
        unread_count: 0,
        is_active: true,
      });
    }

    return {
      _uid: backendContact.id,
      name: backendContact.name,
      // The API sends firstName/lastName (Jackson camelCase off ContactDTO).
      // Reading only the snake_case spelling made these permanently undefined,
      // which blanked the First/Last Name inputs every time an existing contact
      // was opened for editing — and that form refuses to save with both blank.
      first_name: backendContact.firstName ?? backendContact.first_name,
      last_name: backendContact.lastName ?? backendContact.last_name,
      assignedTo: backendContact.assignedTo,
      company: backendContact.company || '',
      // Carried through because the profile shows them. A field the API sends and
      // this function drops is invisible: the page renders blank and nothing errors.
      designation: backendContact.designation,
      status: backendContact.status,
      source: backendContact.source,
      phone_numbers: (backendContact.phoneNumbers || []).map((p: any) => ({
        // API returns camelCase phoneNumber/isPrimary; tolerate snake_case too.
        number: p.phoneNumber || p.number,
        type: p.label?.toLowerCase() || 'mobile',
        is_primary: p.isPrimary ?? p.is_primary ?? false,
        verified: true,
        whatsapp_enabled: p.whatsappCapable || false,
        rcs_enabled: p.rcsCapable || false,
      })),
      email_addresses: (backendContact.emails || []).map((e: any) => ({
        email: e.email,
        type: e.label?.toLowerCase() || 'personal',
        is_primary: e.isPrimary || false,
        verified: true,
      })),
      channels: channels,
      labels: backendContact.tags?.filter((t: string) => t && t.trim() !== '') || [],
      notes: backendContact.notes,
      // Everything the voice agent captured beyond the four fields cpass models
      // by name. This transform is a whitelist, so until now it deleted the
      // field on the way in: the API sent all 13 captured values on every
      // contact and no component could reach them, because by the time a
      // component saw a Contact the data was already gone.
      // Kept raw — it is a JSON *string* on the wire (ContactDTO.customFields
      // is a String off a LONGTEXT column). Parsing lives in utils/capturedFields.
      custom_fields: backendContact.customFields ?? backendContact.custom_fields,
      total_interactions: 0,
      last_contact_at: backendContact.lastContactedAt ? new Date(backendContact.lastContactedAt).toISOString() : undefined,
      is_assigned: false,
      created_at: backendContact.createdAt ? new Date(backendContact.createdAt).toISOString() : new Date().toISOString(),
      updated_at: backendContact.updatedAt ? new Date(backendContact.updatedAt).toISOString() : new Date().toISOString(),
      organization_id: backendContact.organizationId,
    };
  }

  // Get all contacts with filters
  async getContacts(params?: GetContactsParams): Promise<ApiResponse<PaginatedResponse<Contact>>> {
    try {
      const response = await api.get(API_ENDPOINTS.CONTACTS, { params: contactListQuery(params) });

      // Transform backend PageResponse to PaginatedResponse
      const backendData = response.data.data;
      const transformedContacts = (backendData.data || backendData.content || []).map((c: any) => this.transformContact(c));

      const paginatedData: PaginatedResponse<Contact> = {
        data: transformedContacts,
        total: backendData.totalElements || backendData.total || 0,
        page: backendData.page !== undefined ? backendData.page + 1 : 1, // Convert back to 1-based
        // The size the SERVER actually paged by, not the size we asked for. The
        // pager does its "showing X to Y" arithmetic from this, so reporting a
        // requested size the server ignored is how the count came to disagree
        // with the rows on screen.
        perPage: backendData.size || backendData.perPage || 10,
        totalPages: backendData.totalPages || 0,
      };

      return {
        success: response.data.success,
        data: paginatedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch contacts',
      };
    }
  }

  // Get single contact by UID
  /**
   * Fold one contact into another.
   *
   * `survivorId` keeps its own values and gains the other's history; the merged
   * record is ARCHIVED pointing at the survivor, never deleted, so a wrong merge can
   * be undone. Blank fields on the survivor are filled from the loser and nothing
   * already set is overwritten.
   */
  async mergeContacts(survivorId: string, mergeId: string, organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.CONTACT_DETAIL(survivorId)}/merge`,
        { mergeId }, { params: { organizationId } });
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Merge failed' };
    }
  }

  /**
   * Contacts that look like the same person twice.
   *
   * Read-only. Nothing here merges anything — WhatsApp messages, call events and
   * campaign recipients all point at a contact id, so a merge that does not repoint
   * them moves the problem rather than solving it.
   *
   * phoneGroups/emailGroups are the TRUE totals; `groups` is capped at `limit` and
   * sets `truncated`. Read the totals, not the length of the list.
   */
  async getDuplicates(limit = 200, search?: string, organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`${API_ENDPOINTS.CONTACTS}/duplicates`, {
        // Search runs on the SERVER, before the cap — filtering the loaded page would
        // only ever search the 200 groups that happened to arrive.
        params: { limit, ...(search ? { search } : {}), organizationId },
      });
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load duplicates' };
    }
  }

  /**
   * Merge every contact sharing a phone number, server-side, in bounded batches.
   *
   * Different from mergeContacts in a loop, which is what "Merge all shown"
   * does: that is one request per pair over however many groups the page
   * happened to load, and the list is capped at 200. Against the 3,009 groups
   * two overlapping enrols produced on 2026-09-11 that is sixteen page-loads of
   * review nobody was going to do.
   *
   * Previews unless `apply` is true. The response carries groupsRemaining, so
   * the caller repeats until it reaches zero rather than guessing a batch size.
   */
  async mergeDuplicatesByPhone(limit = 500, apply = false, organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.CONTACTS}/duplicates/merge`,
        { limit, apply }, { params: { organizationId } });
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Could not merge duplicates' };
    }
  }

  /**
   * One channel of a contact's history.
   *
   * One call per channel, not a merged feed: WhatsApp is a single indexed query
   * while calls cross two switches over SSH tunnels, so a merged view would make
   * every profile load pay the slowest one. The page renders tabs and fetches the
   * one being looked at.
   *
   * `supported:false` means the channel is not built yet — deliberately different
   * from an empty list, which would say the customer was never contacted there.
   */
  async getTimeline(contactId: string, channel: string, page = 0, size = 50,
                    organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`${API_ENDPOINTS.CONTACT_DETAIL(contactId)}/timeline`, {
        params: { channel, page, size, organizationId },
      });
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load history' };
    }
  }

  /** Leave a remark. Append-only — the sequence is the record. */
  async addNote(contactId: string, body: string, organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.CONTACT_DETAIL(contactId)}/notes`,
        { body }, { params: { organizationId } });
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to save the remark' };
    }
  }

  async getContact(contactUid: string, organizationId?: string): Promise<ApiResponse<Contact>> {
    try {
      const response = await api.get(API_ENDPOINTS.CONTACT_DETAIL(contactUid), {
        params: { organizationId }
      });

      return {
        success: response.data.success,
        data: this.transformContact(response.data.data),
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch contact',
      };
    }
  }

  // Create new contact
  async createContact(data: Partial<Contact>, organizationId?: string): Promise<ApiResponse<Contact>> {
    try {
      const response = await api.post(API_ENDPOINTS.CONTACT_CREATE, data, {
        params: { organizationId }
      });

      return {
        success: response.data.success,
        data: this.transformContact(response.data.data),
        message: response.data.message || 'Contact created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create contact',
      };
    }
  }

  // Update existing contact
  async updateContact(contactUid: string, data: Partial<Contact>, organizationId?: string): Promise<ApiResponse<Contact>> {
    try {
      const response = await api.put(API_ENDPOINTS.CONTACT_UPDATE(contactUid), data, {
        params: { organizationId }
      });

      return {
        success: response.data.success,
        data: this.transformContact(response.data.data),
        message: response.data.message || 'Contact updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update contact',
      };
    }
  }

  // Delete contact
  async deleteContact(contactUid: string, organizationId?: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(API_ENDPOINTS.CONTACT_DELETE(contactUid), {
        params: { organizationId }
      });

      return {
        success: response.data.success,
        message: response.data.message || 'Contact deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete contact',
      };
    }
  }

  // Bulk import contacts
  async bulkImportContacts(file: File, organizationId?: string): Promise<ApiResponse<{ imported: number; failed: number; errors?: string[] }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(API_ENDPOINTS.CONTACTS_BULK_IMPORT, formData, {
        params: { organizationId },
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Bulk import completed',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to import contacts',
      };
    }
  }

  // Get contact interactions/history
  async getContactInteractions(contactUid: string, organizationId?: string): Promise<ApiResponse<ContactInteraction[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.CONTACT_INTERACTIONS(contactUid), {
        params: { organizationId }
      });

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch contact interactions',
      };
    }
  }


  // Get contact statistics
  async getStats(organizationId?: string): Promise<ApiResponse<{
    total: number;
    vip: number;
    blocked: number;
    recentlyAdded: number;
    activeThisMonth: number;
    growthRate: number;
  }>> {
    try {
      const response = await api.get(API_ENDPOINTS.CONTACT_STATS, {
        params: { organizationId }
      });

      // Map backend response to frontend format
      const backendData = response.data.data;
      const mappedData = {
        total: backendData.totalContacts || 0,
        vip: backendData.vipContacts || 0,
        blocked: backendData.blockedContacts || 0,
        recentlyAdded: backendData.recentlyAdded || 0,
        activeThisMonth: backendData.activeContacts || 0,
        growthRate: backendData.growthRatePct ?? 0,
      };

      return {
        success: response.data.success,
        data: mappedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch contact statistics',
      };
    }
  }
}

export const contactsService = new ContactsService();
